// Definicion de columnas elegibles de una tabla. Las que cada persona ve, y
// en que orden, se guardan en su usuario (servidor): la siguen a cualquier
// computador. Ver usar-columnas.ts.

import type {Texto} from '../i18n/idioma';

// Titulos y grupos son bilingues: txt('Estado', 'Status'); se muestran con trx().
export type ColumnaDeTabla<T, O extends string = string> = {
  id: string;
  titulo: Texto | string;
  grupo: Texto | string;
  // Columna por la que se puede ordenar en el servidor; sin ella, no se ordena.
  orden?: O;
  numerica?: boolean;
  porDefecto?: boolean;
  ancho?: number;
  // Nota que sale al pasar el cursor por el titulo: que es y como se llena.
  ayuda?: Texto | string;
  valor: (fila: T) => string;
};

export type DefinicionDeColumnas<T, O extends string = string> = {
  // Clave con la que se guarda la seleccion en las preferencias del usuario.
  clave: string;
  columnas: ColumnaDeTabla<T, O>[];
  grupos: Array<Texto | string>;
  // Columna que no se puede ocultar (la llave de la fila).
  fija: string;
};

export const columnasPorDefecto = <T, O extends string>(definicion: DefinicionDeColumnas<T, O>) =>
  definicion.columnas.filter(c => c.porDefecto).map(c => c.id);

// Deja solo ids validos, sin repetidos y con la columna fija al frente.
export function columnasValidas<T, O extends string>(
  definicion: DefinicionDeColumnas<T, O>,
  ids: readonly string[] | undefined,
): string[] {
  if (!ids || !ids.length) return columnasPorDefecto(definicion);
  const validas = ids.filter(id => definicion.columnas.some(c => c.id === id));
  const unicas = [...new Set(validas)];
  if (!unicas.includes(definicion.fija)) unicas.unshift(definicion.fija);
  return unicas;
}

// Mueve una columna a la posicion de otra (arrastrar y soltar en el encabezado).
export function moverColumna(ids: string[], origen: string, destino: string): string[] {
  if (origen === destino || !ids.includes(origen) || !ids.includes(destino)) return ids;
  const sinOrigen = ids.filter(id => id !== origen);
  const indice = sinOrigen.indexOf(destino);
  return [...sinOrigen.slice(0, indice), origen, ...sinOrigen.slice(indice)];
}
